import {
  FlexColumnCenterBoth,
  FlexRowCenterHorizontal,
  GenericMessageDescriptor,
  Icon,
  IconType,
  StyleUtils,
  useFormatMessageGeneric,
} from '@main/core-ui';
import React, { useMemo } from 'react';
import { useTheme } from 'styled-components';

/**
 * The types of statuses the Result component can display.
 */
export type ResultStatusType = 'success' | 'error' | 'info' | 'warning';

export interface ResultProps {
  /** The status of the result */
  status?: ResultStatusType;
  /** Title text to display */
  title?: GenericMessageDescriptor;
  /** Subtitle text to display */
  subtitle?: GenericMessageDescriptor;
  /** Optional icon override */
  icon?: React.ReactNode;
  /** Additional className */
  className?: string;
  /** Inline styles */
  style?: React.CSSProperties;
}

/**
 * Custom Result component to replace Ant Design's Result
 */
export const Result: React.FC<ResultProps> = ({
  status = 'info',
  title,
  subtitle,
  icon,
  className,
  style,
}) => {
  const { colors } = useTheme();
  const { formatMessageGeneric } = useFormatMessageGeneric();

  const statusIconMap = useMemo<Record<ResultStatusType, React.ReactNode>>(
    () => ({
      success: (
        <Icon
          type={IconType.CheckmarkCircle}
          size={64}
          color={colors.success}
        />
      ),
      error: (
        <Icon
          type={IconType.ExclamationCircle}
          size={64}
          color={colors.danger}
        />
      ),
      warning: (
        <Icon
          type={IconType.ExclamationTriangle}
          size={64}
          color={colors.warning}
        />
      ),
      info: <Icon type={IconType.ICircle} size={64} color={colors.transcend} />,
    }),
    [colors],
  );

  return (
    <FlexColumnCenterBoth
      className={className}
      style={{
        padding: '48px 32px',
        gap: StyleUtils.Spacing.md,
        flexGrow: 1,
        ...style,
      }}
    >
      <FlexRowCenterHorizontal>
        {icon ?? statusIconMap[status]}
      </FlexRowCenterHorizontal>
      {title && <h2>{formatMessageGeneric(title)}</h2>}
      {subtitle && (
        <div style={{ textAlign: 'center' }}>
          {formatMessageGeneric(subtitle)}
        </div>
      )}
    </FlexColumnCenterBoth>
  );
};
